import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UnityLogo } from '@/components/shared/unity-logo';
import { cn } from '@/utils/cn';

/**
 * The frame every auth screen sits in.
 *
 * One card, centred, nothing else on the page. These screens are reached by people
 * who are locked out or in a hurry, and a sidebar they cannot use yet is only
 * something else to read.
 */
export const AuthLayout = ({
  title,
  description,
  children,
  footer,
  cardClassName,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Widen the card for multi-step flows with a stepper. */
  cardClassName?: string;
}) => {
  const { t } = useTranslation(['auth', 'common']);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface-sunken px-4 py-8 sm:py-10">
      <div className="text-center">
        <Link
          to="/"
          className="inline-flex flex-col items-center gap-3 text-center text-fg transition-colors hover:opacity-90"
        >
          <UnityLogo variant="horizontal-color" className="h-10 w-auto" label={t('app.name', { ns: 'common' })} />
          <p className="text-body-sm text-fg-secondary">{t('brand.tagline')}</p>
        </Link>
      </div>

      <Card className={cn('w-full max-w-md', cardClassName)}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>

      {footer ? <div className="text-body-sm text-fg-secondary">{footer}</div> : null}
    </div>
  );
};
